import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, CheckCircle2, ArrowRight, Clock, Shield, Leaf, Star, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const inclusions = [
  "Desk, workstation & surface sanitisation",
  "Restroom deep cleaning & restocking",
  "Kitchen & breakroom cleaning",
  "Vacuuming carpets & mopping hard floors",
  "Rubbish removal & recycling",
  "Glass partitions & internal windows",
  "High-touch point disinfection (door handles, switches)",
  "Reception & meeting room presentation",
];

const industries = [
  "Corporate offices & coworking spaces",
  "Medical & dental clinics",
  "Retail stores & showrooms",
  "Gyms & fitness studios",
  "Schools & childcare centres",
  "Warehouses & industrial sites",
];

const whyUs = [
  { icon: Clock, title: "Flexible scheduling", desc: "Daily, weekly or fortnightly — before or after hours so we never disrupt your team." },
  { icon: Shield, title: "Fully insured & police-checked", desc: "$20M public liability and vetted staff you can trust with keys and alarm codes." },
  { icon: Leaf, title: "Eco-friendly products", desc: "Hospital-grade disinfectants that are safe for staff, kids, and the planet." },
  { icon: Star, title: "Melbourne-local team", desc: "Same crew each visit. We know your space and your standards." },
];

const faqs = [
  {
    q: "How much does office cleaning cost in Melbourne?",
    a: "Most Melbourne offices fall between $35–$55 per hour, or $0.30–$0.80 per sqm for regular contracts. We provide a free, fixed-price quote based on size, frequency, and inclusions — no surprises.",
  },
  {
    q: "Do you clean after hours?",
    a: "Yes. Most of our office clients are serviced between 5pm and 7am so your team walks into a spotless workspace each morning.",
  },
  {
    q: "Which Melbourne suburbs do you cover?",
    a: "We cover the Melbourne CBD, Docklands, Port Melbourne, South Yarra, Richmond, St Kilda, and most metro suburbs within 25km of the city.",
  },
  {
    q: "Do you supply consumables like toilet paper and soap?",
    a: "Yes — we can manage all consumables and restock as part of your contract, billed at wholesale cost.",
  },
  {
    q: "Can I get a one-off office clean?",
    a: "Absolutely. We do one-off spring cleans, post-construction cleans, and end-of-lease office cleans alongside our regular contracts.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: "Office Cleaning Melbourne",
      serviceType: "Commercial Office Cleaning",
      provider: {
        "@type": "LocalBusiness",
        name: "DH Cleaning Services",
        areaServed: { "@type": "City", name: "Melbourne" },
        priceRange: "$$",
      },
      areaServed: { "@type": "City", name: "Melbourne" },
      description: "Professional office cleaning in Melbourne. After-hours service, fully insured, eco-friendly. Free quote.",
      offers: { "@type": "Offer", priceCurrency: "AUD", price: "35", description: "From $35/hour" },
    },
    {
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://dhcleaningservices.com.au/" },
        { "@type": "ListItem", position: 2, name: "Services", item: "https://dhcleaningservices.com.au/services" },
        { "@type": "ListItem", position: 3, name: "Office Cleaning Melbourne", item: "https://dhcleaningservices.com.au/services/office-cleaning-melbourne" },
      ],
    },
  ],
};

const OfficeCleaningMelbourne = () => {
  return (
    <Layout>
      <SEO
        title="Office Cleaning Melbourne — From $35/hr"
        description="Professional office cleaning in Melbourne. After-hours service, fully insured, eco-friendly products. Free fixed-price quote in 24 hours."
        canonical="/services/office-cleaning-melbourne"
        keywords="office cleaning melbourne, commercial cleaning melbourne, office cleaners melbourne, melbourne CBD office cleaning"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-16 lg:py-24">
        <div className="container">
          <motion.div {...fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-4">
              <Building2 className="h-4 w-4" /> Melbourne Office Cleaning
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Office Cleaning in Melbourne
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Reliable, after-hours office cleaning that keeps your workplace fresh, hygienic, and impressive — from $35/hour.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/quote">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/contact">Talk to Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-16 lg:py-20">
        <div className="container">
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl font-bold text-foreground text-center mb-12">
            Why Melbourne businesses choose DH
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyUs.map((item, i) => (
              <motion.div key={item.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <item.icon className="h-6 w-6 text-primary" aria-hidden="true" />
                    </div>
                    <h3 className="font-display font-semibold text-lg text-foreground mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="container grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div {...fadeUp}>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">What's included</h2>
            <p className="text-muted-foreground mb-6">
              A standard office clean covers everything below. Need extras like carpet steam cleaning, window cleaning, or strip-and-seal floors? We bundle them into your contract at a discounted rate.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {inclusions.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" /> {item}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">Industries we clean</h2>
            <p className="text-muted-foreground mb-6">
              From single-tenant offices to multi-floor sites, we tailor a scope and schedule to your industry's standards.
            </p>
            <ul className="space-y-3">
              {industries.map((item) => (
                <li key={item} className="flex items-center gap-2 text-foreground">
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0" aria-hidden="true" /> {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 lg:py-20">
        <div className="container">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">Transparent Melbourne pricing</h2>
            <p className="text-muted-foreground">
              No lock-in contracts. No hidden fees. Just an honest, fixed-price quote based on your space and frequency.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { label: "Small office", size: "Up to 150 sqm", price: "From $90", per: "per clean" },
              { label: "Medium office", size: "150–400 sqm", price: "From $180", per: "per clean", highlight: true },
              { label: "Large office", size: "400 sqm+", price: "Custom", per: "tailored quote" },
            ].map((tier) => (
              <Card key={tier.label} className={tier.highlight ? "border-primary border-2" : ""}>
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-8 w-8 text-primary mx-auto mb-2" aria-hidden="true" />
                  <h3 className="font-display font-bold text-lg text-foreground">{tier.label}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{tier.size}</p>
                  <div className="font-display text-2xl font-bold text-primary">{tier.price}</div>
                  <p className="text-xs text-muted-foreground">{tier.per}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="container max-w-3xl">
          <motion.h2 {...fadeUp} className="font-display text-3xl sm:text-4xl font-bold text-foreground text-center mb-10">
            Office cleaning FAQs
          </motion.h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-display">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-primary text-primary-foreground">
        <div className="container text-center max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">Ready for a cleaner office?</h2>
          <p className="text-lg mb-6 opacity-90">
            Get a free fixed-price quote within 24 hours. No obligation.
          </p>
          <Button asChild size="lg" variant="secondary">
            <Link to="/quote">Request a Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default OfficeCleaningMelbourne;
